import { Venues } from '@weddingspot/ws-api-client';
import { VenueHighlight } from '../components/venue-highlights';
import { RecommendedVenuesProps } from '../components/venue-recommendations';
import { EventProps } from '../components/venue-additional-details/upcoming-events';

export enum VenueService {
    indoorReception = 'indoor_reception',
    outdoorReception = 'outdoor_reception',
    indoorCeremony = 'indoor_ceremony',
    outdoorCeremony = 'outdoor_ceremony',
    cocktailHour = 'cocktail_hour',
    privateCeremony = 'private_ceremony',
}

export enum VenueRDService {
    indoorRehearsalDinner = 'indoor_rehearsal_dinner',
    outdoorRehearsalDinner = 'outdoor_rehearsal_dinner',
}

export const IndoorVenueServiceTypes = [VenueService.indoorCeremony, VenueService.indoorReception, VenueRDService.indoorRehearsalDinner];

export const OutdoorVenueServiceTypes = [
    VenueService.outdoorCeremony,
    VenueService.outdoorReception,
    VenueRDService.outdoorRehearsalDinner,
];

export const CeremonyServiceTypes = [VenueService.indoorCeremony, VenueService.outdoorCeremony];

export const ReceptionServiceTypes = [VenueService.indoorReception, VenueService.outdoorReception];
export const RDServiceTypes = [VenueRDService.indoorRehearsalDinner, VenueRDService.outdoorRehearsalDinner];

export enum VenueCatering {
    BYO = 'byo',
    list = 'list',
    provided = 'provided',
}

export enum VenueAlcohol {
    byo = 'byo',
    provided = 'provided',
    providedByDrink = 'provided_by_drink',
}

export interface VenuePackage {
    id: number;
    title: string;
    starting_price: string;
    description: string;
    content_link: string;
}

export interface VenueVideo {
    id: number;
    name: string;
    embedded_video: string;
}

export interface BreadcrumbLink {
    label: string;
    url: string;
}

export interface Discount {
    title: string;
    description: string;
}

export interface VenueImageData {
    id: number;
    image: string;
    crop_image: string;
    main_image: string;
    thumbnail: string;
    mini_thumbnail: string;
    portrait_image: string;
    photo_credit: string;
    storage_url: string;
    order: number;
    provided_by_venue: boolean;
    photo_source: string;
    landscape_image?: string;
}

export interface VenueDetails extends RecommendedVenuesProps {
    id: number;
    csn_venue_id?: string;
    name: string;

    market: string;

    city: string | null;
    state: string | null;
    province: string | null;
    country: string;
    address_1: string | null;
    address_2: string | null;
    zip_code: string | null;
    max_capacity: number;

    catering: VenueCatering[];
    alcohol: VenueAlcohol[];
    packages: VenuePackage[];
    is_favorite?: boolean;
    is_priced: boolean;
    is_destination: boolean;
    starting_price_per_person_rds: number;
    images: VenueImageData[];

    description: string;
    description_rds: string | null;
    services: {
        id: number;
        name: string;
        service_type: VenueService;
        service_type_display_name: string;
        capacity: number;
    }[];
    services_rds: {
        id: number;
        name: string;
        service_type: VenueRDService;
        service_type_display_name: string;
        capacity: number;
    }[];

    cost_min: number;
    cost_max: number;
    amenities: {
        id: number;
        type: string;
        name: string;
    }[];

    special_restrictions: {
        id: number;
        type: string;
        name: string;
    }[];

    styles: {
        id: number;
        type: string;
        name: string;
        long_name: string;
    }[];
    last_updated_date: string;

    phone_number: string;
    region: string;
    website: string | null;
    ceremony: string;
    reception: string;
    wedding_spot_awards_years: number[];
    awards: Venues.VenueAward[];
    hide_wedding_costs: boolean;
    hide_guest_rooms: boolean;
    time_restrictions_str: string;
    rental_fee_description: string;
    availability: { [date: string]: number } | null;
    capacity: string[];
    timeRestrictions: string[];
    longitude: number;
    latitude: number;
    videos: VenueVideo[];
    no_longer_doing_weddings: boolean;
    venueHighlights: VenueHighlight[];
    discounts: Discount[];
    disableRecommendations: boolean;
    upcoming_events: EventProps[];
    testFlagDetails?: boolean;
    is_not_available?: boolean;

    // budget props
    budgetRangeMin: number | null;
    budgetCapacity: number | null;

    // personalized details
    notifications?: Venues.VenueProfileNotification[];
    userSpotEstimate?: number | null;
    showAdditionalPriceVenueButton?: boolean;
    showGuestRoomsRecommendations?: boolean;
    showWsRfpForm?: boolean;
    showSleepingRoomsOnVenueProfile?: boolean;
    testGuestRoomsIntentCta?: 'A' | 'B';
    intentCTAonPostOutreachStep1?: 'A' | 'B';
    testFlagPersonalized?: boolean;
    persinalizedInfoLoadSucceeded: boolean | undefined;
    simplifyOutreachTestValue2?: 'A' | 'B';
    intentCTAStepRemoved?: 'A' | 'B';
    // Links
    facebookLink: string | null;
    instagramLink: string | null;
    pinterestLink: string | null;

    // RDV Props
    has_weddings: boolean;
    has_rds: boolean;

    meta_description?: string;
    page_title?: string;
    schema_org?: string;
    canonical_url?: string;
}

export interface VenueProfile extends VenueDetails {
    activeSlide: number;
    activeTabIndex: number;
    isMoreTabOpen: boolean;
    isPhoneShown: boolean;
    profileLoaded: boolean;
    inquiryModalShowing: boolean;
    renderRdv: boolean;
}
